import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { GIButton } from 'app-components';
import { useAppSelector } from '../../../../hooks';
import { environmentSelector } from '@selectors/commonSelectors';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { useDynamicSecurityQuestions, FIELD_IDS, validations } from '../hooks/useDynamicSecurityQuestions';
import { configs } from '@constants/configs/configs';
import DatePicker from '@lib/inputs/react-datepicker';
import ReCaptcha from '@components/common/form/recaptcha/ReCaptcha';
import { BREAK_POINTS } from '@constants/common/breakpoints';
import { ValidationErrorMessage } from '@modules/members/utils';
import { useAccessCode } from '@components/accessCode/hooks/useAccessCode';
import { formatDate } from '@utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';
import { ErrorModal } from '@components/errorModal/ErrorModal';
import ResultDialog from '@components/common/modals/ResultDialog';
import { GENDER_TYPES } from '@constants/ssap/miscellaneous/miscellaneous';


const StyledContainer = styled.div`
    padding: 15px;
    border: 1px solid ${({ theme }) => theme.gray8};
    border-radius: 6px;
    margin-bottom: 15px;
    .input-label-container {
        text-align: right;
        margin: auto;
    }
    @media (max-width: ${BREAK_POINTS.sm}) {
        .input-label-container {
            text-align: left;
        }
    }
    .warningText {
        display: inline-block;
        padding-left: 25px;
    }
    .required-true::after{ 
        color: ${({ theme }) => theme.dangerMain};
    }
    .warningMessage{
        color: ${({ theme }) => theme.dangerMain};
    }
    .gi-input {
        text-align: left;
        margin-top: 0px;
        width: fit-content;
        display: inline-block;
    }
    .react-datepicker-wrapper {
        padding-left:0px !important;
    }
    .row{
        padding:5px;
    }
    .gender-selection {
        display: flex;
        align-items: center;
    }
    .gender-option {
        margin-right: 15px;
        display: flex;
        align-items: center;
    }
    .gender-option input {
        margin-right: 5px;
    }
    .input-large {
        width: 210px;
    }
    .center-text{
        text-align: -webkit-center !important;
    }
    @media (max-width: ${BREAK_POINTS.sm}) {
    .input-large {
        width: 100%;
    }
    .warningText{
        padding: 5px 0px !important;
    }
    }
`;

const StyledButton = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    padding-right: 25px;
    padding-bottom: 25px;
    margin-top:2rem;
`;

const StyledAlertContainer = styled.div`
    border-left: 3px solid ${({ theme }) => theme.infoMain} !important;
    color: ${({ theme }) => theme.infoDarker} !important;
    background: ${({ theme }) => theme.infoLightest} !important;
`;

const MAX_AGE_LIMIT = 105;

const securityQuestions = () => {
    const location = useLocation();
    const { randomQuestions, encryptedActivationId, reference, accessCodeValues } = location.state;
    const [errorMessage, setErrorMessage] = useState('');
    const [inValidAnswers, setInValidAnswers] = useState<string[]>([]);
    const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
    const navigate = useNavigate();
    const captchaHandle = useRef(null);
    
    const {
        accessCodeExpandedViewEnabled,
        hideCaptcha,
        properties: { RECAPTCHA_SITE_KEY },
        language,
    } = useAppSelector(environmentSelector);
    const { verifyAccessCode } = useAccessCode();

    let [reCaptchaValue, setReCaptchaValue] = useState('');
    const { submitReferralSecurity, securityQuestions, setSecurityQuestions, isSubmitEnabled, resetSecurityQuestionValues,
        hasFirstNameWarning, firstNameWarning, hasLastNameWarning, lastNameWarning,
        hasDOBWarning, DOBWaning, hasSSNWarning, SSNWarning,
        hasZipCodeWarning, zipCodeWarning, hasEmailWarning, emailWarning,
        hasPhoneWarning, phoneWarning, hasCountyWarning, countyWarning,
        hasMembersWarning, membersWarning, hasMedicaidIdWarning, medicaidIdWarning, hasApplicationCaseNumberWarning, applicationCaseNumberWarning
    } = useDynamicSecurityQuestions(randomQuestions);

    const reCaptchaWrapperExpandedViewEnabledClass = 'desktop:grid-col-8 tablet:grid-col-8 mobile:grid-col-8 grid-col-6 margin-tb-10';
    const reCaptchaWrapperNonExpandedViewEnabledClass = 'desktop:grid-col-8 tablet:grid-col-8 mobile:grid-col-8 grid-col-6 margin-b-20';
    const reCaptchaWrapperClass = accessCodeExpandedViewEnabled ? reCaptchaWrapperExpandedViewEnabledClass : reCaptchaWrapperNonExpandedViewEnabledClass;

    useEffect(()=>{
        const isBrowserRefresh = sessionStorage.getItem('securityQuestionRefresh') === 'true' && performance.navigation.type === performance.navigation.TYPE_RELOAD;
        if (isBrowserRefresh) {
            questionRefresh();
        }
        sessionStorage.setItem('securityQuestionRefresh', 'true');
    },[]);

    const isSubmitButtonDisabled = () => {
        return hideCaptcha ? !isSubmitEnabled() : !(isSubmitEnabled() && securityQuestions.isReCaptchaValid);
    };

    const handleValueChange = (e: { target: any; }) => {
        let { id, value } = e.target;
        const isValid = validations[id].test(value);
        setSecurityQuestions(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                value: value,
                isEmpty: value.trim() === '',
                isValidInput: isValid,
            }
        }));
    };

    const handleDateChange = (e) => {
        const formattedValue = formatDate(e, MM_SLASH_DD_SLASH_YYYY);
        const isValid = validations['birthDate'].test(formattedValue) && isValidDOB(formattedValue);
    
        setSecurityQuestions((prev) => ({
            ...prev,
            birthDate: {
                ...prev.birthDate,
                value: e,
                isEmpty: !formattedValue.trim(),
                isValidInput: isValid,
            },
        }));
    };

    function isValidDOB(dobStr: string | number | Date) {
        const dob = new Date(dobStr);
        const today = new Date();
        const pastLimit = new Date(today.getFullYear() - MAX_AGE_LIMIT, today.getMonth(), today.getDate());
        return dob >= pastLimit && dob <= today;
    }

    const handleSSNChange = (e: { target: { value: string; }; }) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
        const formatted =
            digits.length < 4
                ? digits
                : digits.length < 6
                    ? `${digits.slice(0, 3)}-${digits.slice(3)}`
                    : `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;

        handleValueChange({
            target: {
                id: 'ssn',
                value: formatted,
            },
        });
    };

    const handlePhoneInputChange = (e: { target: { value: string; }; }) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
        const formatted =
            digits.length < 4
                ? digits
                : digits.length < 7
                    ? `(${digits.slice(0, 3)}) ${digits.slice(3)}`
                    : `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;

        handleValueChange({
            target: {
                id: 'phoneNumber',
                value: formatted,
            },
        });
    };

    const handleSubmit = async () => {
        const result = await submitReferralSecurity(reCaptchaValue, encryptedActivationId, reference);
    
        if (result?.errorMessage) {
            const { errorMessage, validationErrors } = result;
    
            const wrongAnswers: string[] = validationErrors && typeof validationErrors === 'object'
                ? Object.values(validationErrors)
                : [];
    
            setInValidAnswers(wrongAnswers);
            setErrorMessage(errorMessage);
            captchaHandle.current?.reset();
            setReCaptchaValue('');
            setSecurityQuestions(prev => ({ ...prev, isReCaptchaValid: false }));
            return;
        }
    };

    const handleErrorModalClose = (e?: React.MouseEvent<HTMLElement> | null) => {
        window.location.href = configs.routes.DYNAMIC_SECURITY_QUESTIONS; 
    };

    const questionRefresh = async () => {
        const result = await verifyAccessCode(accessCodeValues);
        if (!result) return;
        const { securityQuestions, encryptedActivationId, reference, errorMessage } = result;
        resetSecurityQuestionValues();
        if (securityQuestions) {
            navigate(configs.routes.DYNAMIC_SECURITY_QUESTIONS, {
                state: { randomQuestions: securityQuestions, encryptedActivationId, reference, accessCodeValues },
                replace: true 
            });
            return;
        }
        if (errorMessage) {
            navigate(configs.routes.ACCESS_CODE);
        }
    };

    const handleSuccessModalClose = (e?: React.MouseEvent<HTMLElement> | null) => {
        e?.preventDefault();
        setIsSubmitSuccess(false);
    };

    const renderInvalidAnswersMessages = () => (
        inValidAnswers.map((msg, index) => (
            <p className="warningMessage" key={index}>{msg}</p>
        ))
    );

    return (
        <StyledContainer>
            <h4 className="padding-2 padding-b-20 padding-l-0" data-testid="referral-questions-header">
                <FormattedMessage id='dynamicSecurityQuestion.securityQuestions' />
            </h4>
            <StyledAlertContainer className="alert padding-2">
                <li>
                    <FormattedMessage
                        id='dynamicSecurityQuestion.privacy'
                    />
                </li>
                <li>
                    <FormattedMessage
                        id='dynamicSecurityQuestion.successfullyAnswered'
                    />
                </li>
            </StyledAlertContainer>
            <ConditionalRendering isTrue={inValidAnswers.length > 0}>
                {renderInvalidAnswersMessages()}
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.FIRST_NAME)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.firstName' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9' data-testid='referral-questions-firstName'>
                        <input
                            className='input-large'
                            type="text"
                            id={FIELD_IDS.FIRST_NAME}
                            value={securityQuestions?.[FIELD_IDS.FIRST_NAME]?.value}
                            onChange={handleValueChange}
                        />
                        <ValidationErrorMessage
                            errorWrapperClasses={['warningText']}
                            isVisible={hasFirstNameWarning}
                            messageText={firstNameWarning}
                        />
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.LAST_NAME)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.lastName' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9 ' data-testid='referral-questions-lastName'>
                        <input
                            className='input-large'
                            type="text"
                            id={FIELD_IDS.LAST_NAME}
                            value={securityQuestions.lastName.value}
                            onChange={handleValueChange}
                        />
                        <ValidationErrorMessage
                            errorWrapperClasses={['warningText']}
                            isVisible={hasLastNameWarning}
                            messageText={lastNameWarning}
                        />
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.DATE_OF_BIRTH)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.dob' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9 ' data-testid='referral-questions-dob'>
                        <DatePicker
                            id="question3"
                            name={FIELD_IDS.DATE_OF_BIRTH}
                            type="text"
                            placeholder="MM/DD/YYYY"
                            showIcon={false}
                            pattern="MM/DD/YYYY"
                            value={securityQuestions.birthDate.value}
                            withMask
                            showYearDropdown={false}
                            isRequired={true}
                            open={false}
                            onDateChange={handleDateChange}
                        />
                        <ValidationErrorMessage
                            errorWrapperClasses={['warningText']}
                            isVisible={hasDOBWarning}
                            messageText={DOBWaning}
                        />
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.SSN)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.ssn' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9 ' data-testid='referral-questions-ssn'>
                        <input
                            type="text"
                            id={FIELD_IDS.SSN}
                            value={securityQuestions.ssn.value}
                            onChange={handleSSNChange}
                            placeholder="sss-ss-ssss"
                            inputMode="numeric"
                            maxLength={11}
                            className='input-large'
                        />

                        <ValidationErrorMessage
                            errorWrapperClasses={['warningText']}
                            isVisible={hasSSNWarning}
                            messageText={SSNWarning}
                        />
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.ZIP_CODE)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.zipCode' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9 ' data-testid='referral-questions-zipCode'>
                        <input
                            type="text"
                            id={FIELD_IDS.ZIP_CODE}
                            maxLength={5}
                            value={securityQuestions.zipCode.value}
                            onChange={handleValueChange}
                            className='input-large'
                        />
                        <ValidationErrorMessage
                            errorWrapperClasses={['warningText']}
                            isVisible={hasZipCodeWarning}
                            messageText={zipCodeWarning}
                        />
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.GENDER)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.gender' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9 gender-selection' data-testid='referral-questions-gender'>
                        <label className="gender-option">
                            <input
                                type="radio"
                                id={GENDER_TYPES.MALE}
                                name="gender"
                                value={GENDER_TYPES.MALE}
                                checked={securityQuestions.gender === GENDER_TYPES.MALE}
                                onChange={(e) => setSecurityQuestions((prev) => ({
                                    ...prev,
                                    'gender': e.target.value
                                }))}
                            />
                            <FormattedMessage id='dynamicSecurityQuestion.male' />
                        </label>

                        <label className="gender-option">
                            <input
                                type="radio"
                                id={GENDER_TYPES.FEMALE}
                                name="gender"
                                value={GENDER_TYPES.FEMALE}
                                checked={securityQuestions.gender === GENDER_TYPES.FEMALE}
                                onChange={(e) => setSecurityQuestions((prev) => ({
                                    ...prev,
                                    'gender': e.target.value
                                }))}
                            />
                            <FormattedMessage id='dynamicSecurityQuestion.female' />
                        </label>
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.EMAIL)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.email' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9 ' data-testid='referral-questions-email'>
                        <input
                            className='input-large'
                            type="email"
                            id={FIELD_IDS.EMAIL}
                            value={securityQuestions[FIELD_IDS.EMAIL].value}
                            onChange={handleValueChange}
                        />
                        <ValidationErrorMessage
                            errorWrapperClasses={['warningText']}
                            isVisible={hasEmailWarning}
                            messageText={emailWarning}
                        />
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.PHONE)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.phone' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9 ' data-testid='referral-questions-phone'>
                        <input
                            type="tel"
                            id={FIELD_IDS.PHONE}
                            value={securityQuestions.phoneNumber.value}
                            onChange={handlePhoneInputChange}
                            placeholder="(999) 999-9999"
                            inputMode="numeric"
                            maxLength={14} // Max formatted length: (999) 999-9999
                        />
                        <ValidationErrorMessage
                            errorWrapperClasses={['warningText']}
                            isVisible={hasPhoneWarning}
                            messageText={phoneWarning}
                        />
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.COUNTY)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.county' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9 ' data-testid='referral-questions-county'>
                        <input
                            className='input-large'
                            type="text"
                            id={FIELD_IDS.COUNTY}
                            value={securityQuestions.county.value}
                            onChange={handleValueChange}
                        />
                        <ValidationErrorMessage
                            errorWrapperClasses={['warningText']}
                            isVisible={hasCountyWarning}
                            messageText={countyWarning}
                        />
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.MEMBERS)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.numberOfMembers' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9 ' data-testid='referral-questions-total-members'>
                        <input
                            className='input-large'
                            type="text"
                            id={FIELD_IDS.MEMBERS}
                            value={securityQuestions[FIELD_IDS.MEMBERS].value}
                            onChange={handleValueChange}
                            maxLength={2}
                        />
                        <ValidationErrorMessage
                            errorWrapperClasses={['warningText']}
                            isVisible={hasMembersWarning}
                            messageText={membersWarning}
                        />
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.MEDICAID_ID)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.medicaidId' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9 ' data-testid='referral-questions-medicaidId'>
                        <input
                            className='input-large'
                            type="text"
                            id={FIELD_IDS.MEDICAID_ID}
                            value={securityQuestions.medicaidId.value}
                            onChange={handleValueChange}
                        />
                        <ValidationErrorMessage
                            errorWrapperClasses={['warningText']}
                            isVisible={hasMedicaidIdWarning}
                            messageText={
                                medicaidIdWarning
                            }
                        />
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={randomQuestions.includes(FIELD_IDS.APPLICATION_CASE_NUMBER)}>
                <div className='row'>
                    <div className='col-xs-12 col-md-3 col-lg-3 input-label-container'>
                        <label className='required-true'>
                            <FormattedMessage id='dynamicSecurityQuestion.applicationCaseNumber' />
                        </label>
                    </div>
                    <div className='col-xs-12 col-md-9 col-lg-9 ' data-testid='referral-questions-caseNumber'>
                        <input
                            type="text"
                            className='input-large'
                            id={FIELD_IDS.APPLICATION_CASE_NUMBER}
                            value={securityQuestions[FIELD_IDS.APPLICATION_CASE_NUMBER].value}
                            onChange={handleValueChange}
                        />
                        <ValidationErrorMessage
                            errorWrapperClasses={['warningText']}
                            isVisible={hasApplicationCaseNumberWarning}
                            messageText={
                                applicationCaseNumberWarning
                            }
                        />
                    </div>
                </div>
            </ConditionalRendering>
            <ConditionalRendering isTrue={!hideCaptcha}>
                <ReCaptcha
                    ref={captchaHandle}
                    sitekey={RECAPTCHA_SITE_KEY}
                    key={language}
                    lang={language}
                    setCaptchaValue={setReCaptchaValue}
                    onValidated={() => {
                        setSecurityQuestions(prev => ({ ...prev, isReCaptchaValid: true }));
                    }
                    }
                    onExpired={() =>
                    {
                        setSecurityQuestions(prev => ({ ...prev, isReCaptchaValid: false }));
                    }
                    }
                    outerClass={['center-text col-xs-12 col-md-6 col-lg-6']}
                    wrapperClass={[reCaptchaWrapperClass]}
                />
            </ConditionalRendering>
            <StyledButton className='col-xs-12 col-md-6 col-lg-6'>
                <GIButton
                    type="submit"
                    id="securityQuestionInfo"
                    labelId="button.submit"
                    onClickHandler={handleSubmit}
                    isDisabled={isSubmitButtonDisabled()}
                    data-testid={'securityQuestionInfo'}
                />
            </StyledButton>
            <ConditionalRendering isTrue={errorMessage.length > 0}>
                <ErrorModal onClose={handleErrorModalClose} errorText={errorMessage} titleId={'common.modal.failure'} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isSubmitSuccess}>
                <ResultDialog onClose={handleSuccessModalClose} headerId={'common.modal.success'} message='' />
            </ConditionalRendering>
        </StyledContainer >

    );
};

export default securityQuestions;